<template>
  <div id="fusion_enterprise_contact_details_container" class="w-full h-full flex flex-col overflow-y-auto">
    <template v-if="contact">
      <!-- Header: avatar + name + DMS badge -->
      <div class="flex flex-col items-center pt-4 pb-2 px-4 gap-3">
        <div class="relative shrink-0">
          <messenger-avatar :name="contact.fullName" :size="48" :bg-color="avatar_color" />
          <!-- TODO: render presence dot when online status is available from the API -->
        </div>
        <p class="text-[28px] font-normal leading-[1.28] text-text-default text-center">{{ contact.fullName }}</p>
      </div>

      <!-- Action buttons -->
      <div class="px-4 py-2 flex gap-2 justify-center">
        <!-- TODO: "Add to Customer chat" — hidden until backend is implemented -->
        <button type="button" :disabled="!is_messaging_enabled"
                class="h-10 px-3 rounded-xl bg-btn-primary hover:bg-btn-primary/90 text-white text-base inline-flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-btn-primary"
                @click.prevent="sendMessage">
          <img :src="message_icon" alt="" class="w-6 h-6 brightness-0 invert" />
          <span>{{ $t('messaging.contactDetails.message') }}</span>
        </button>
      </div>

      <!-- Info block: Fusion Notes / Mobile / Email -->
      <div class="px-6 pb-2 flex flex-col">
        <!-- TODO: Fusion Notes — hidden until backend is implemented -->
        <!-- TODO: Mobile + Opted-In badge — hidden until backend is implemented -->
        <div class="border-b border-border-separator py-2 flex flex-col gap-1">
          <p class="text-[13px] font-medium text-text-subtle leading-[1.35] tracking-[0.16px]">{{ $t('messaging.contactDetails.email') }}</p>
          <p class="text-base text-text-default leading-6 break-all">{{ contact_email || '—' }}</p>
        </div>
      </div>

      <!-- Shared chats -->
      <div class="px-[18px] pb-4 flex flex-col">
        <div class="pt-4 pb-2">
          <p class="text-base font-semibold text-text-default leading-[1.375]">{{ $t('messaging.contactDetails.sharedChats') }}</p>
        </div>
        <message-thread
          v-for="thread in shared_threads_with_names"
          :key="`contact_shared_thread_${thread.threadId}`"
          class="hover:bg-black/5"
          :thread-type="thread._displayType"
          :current-user-guid="current_user_guid"
          :users-by-guid="users_by_guid"
          :message-thread="thread"
          :avatar-colors="avatar_colors"
          :last-message="thread.lastMessagePreview || null"
          :last-message-at="thread.lastMessageAt || null"
          :is-current="thread.threadId === active_thread_id"
          :clickable="shared_chats_clickable"
          @selectThread="openSharedThread(thread.threadId)" />
        <p v-if="!$FUSarrayHasEntries(shared_threads)" class="text-sm text-text-placeholder">
          No shared chats yet.
        </p>
      </div>
    </template>

    <p v-else class="text-center text-text-default/70 my-5">{{ $t('messaging.contactDetails.notFound') }}</p>
  </div>
</template>

<style scoped>
</style>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@store/main';
import MessengerAvatar from '@components/Messaging/MessengerAvatar.vue';
import MessageThread from '@components/Messaging/MessageThread.vue';
import Message3Icon from '@images/message_3_line.svg';

export default {
  name: 'EnterpriseContactDetails',
  components: {
    'messenger-avatar': MessengerAvatar,
    'message-thread': MessageThread,
  },
  beforeUnmount() {
    // Clear the guid on any unmount path so it isn't stale if a later flow checks
    // open_contact_guid before re-mounting this view.
    this.open_contact_guid = null;
  },
  data() {
    return {
      message_icon: Message3Icon,
    }
  },
  computed: {
    ...mapStores(useMainStore),
    enterprise_users_data() {
      return this.mainStore.enterprise_users_data;
    },
    user_access_info() {
      return this.mainStore.user_access_info;
    },
    user_threads() {
      return this.mainStore.user_threads;
    },
    channel_threads() {
      return this.mainStore.channel_threads;
    },
    object_threads() {
      return this.mainStore.object_threads;
    },
    current_user_guid() {
      return this.user_access_info?.userId || null;
    },
    open_contact_guid: {
      get() { return this.mainStore.open_contact_guid; },
      set(value) { this.mainStore.open_contact_guid = value; }
    },
    internal_messaging_state: {
      get() { return this.mainStore.internal_messaging_state; },
      set(value) { this.mainStore.internal_messaging_state = value; }
    },
    pending_new_message_recipient_guid: {
      get() { return this.mainStore.pending_new_message_recipient_guid; },
      set(value) { this.mainStore.pending_new_message_recipient_guid = value; }
    },
    contact_return_guid: {
      get() { return this.mainStore.contact_return_guid; },
      set(value) { this.mainStore.contact_return_guid = value; }
    },
    active_thread_id() {
      return this.mainStore.active_thread_id;
    },
    contact_origin_view() {
      return this.mainStore.contact_origin_view;
    },
    shared_chats_clickable() {
      // When this profile was opened from a thread/channel participant list (Chat Details), every
      // shared chat is display-only — clicking any of them would loop the user back into a chat,
      // then its details, then a participant, and so on. Block navigation on all rows in that case.
      // Opened any other way (e.g. the Contacts list), the rows navigate normally.
      return this.contact_origin_view !== 'ChatDetails';
    },
    contact() {
      const guid = this.open_contact_guid;
      if(!guid) return null;
      const enterpriseUsers = this.enterprise_users_data?.enterpriseUsers || [];
      return enterpriseUsers.find(u => u.personGuid === guid) || null;
    },
    contact_phone() {
      // Defensive lookup — the enterprise user payload doesn't currently expose a phone field
      // in any code we own, but mobile/mobilePhone/phone are the obvious candidates if the API
      // starts returning one.
      const c = this.contact;
      if(!c) return null;
      return c.mobile || c.mobilePhone || c.phone || null;
    },
    contact_email() {
      // The enterprise user payload currently stores the email address in userId.
      // Fall back to the email field if the API starts populating it directly.
      const c = this.contact;
      if(!c) return null;
      return c.email || c.userId || null;
    },
    is_messaging_enabled() {
      return Boolean(this.contact?.messagingEnabled);
    },
    avatar_color() {
      const c = this.contact;
      const palette = this.$FUSfusionAvatarColorsHex || [];
      if(!c || !palette.length) return '#49A4E3';
      // Reuse the same index-by-position approach as ContactsList.contacts_sorted so colors
      // stay consistent when navigating between the list and the details page.
      // IMPORTANT: the filter must match ContactsList.contacts_sorted exactly (including the
      // current_user_guid exclusion). Otherwise any contact alphabetically after the current
      // user would land at an off-by-one index and get a different palette color than the list.
      const self = this;
      const enterpriseUsers = this.enterprise_users_data?.enterpriseUsers || [];
      const filtered = enterpriseUsers
        .filter(u => u.messagingEnabled
                  && u.personGuid
                  && u.personGuid !== self.current_user_guid
                  && u.personGuid.indexOf('system_') < 0)
        .slice()
        .sort((a, b) => (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase()));
      const idx = filtered.findIndex(u => u.personGuid === c.personGuid);
      return palette[Math.max(idx, 0) % palette.length];
    },
    users_by_guid() {
      const users = this.enterprise_users_data?.enterpriseUsers || [];
      const map = {};
      users.forEach(u => {
        if(u.personGuid) map[u.personGuid] = u.friendlyName || u.fullName;
      });
      return map;
    },
    avatar_colors() {
      const palette = this.$FUSfusionAvatarColorsHex || [];
      if(!palette.length) return {};
      const users = this.enterprise_users_data?.enterpriseUsers || [];
      const sorted = users
        .filter(u => u.personGuid && u.personGuid.indexOf('system_') < 0)
        .slice()
        .sort((a, b) => (a.fullName || '').toLowerCase().localeCompare((b.fullName || '').toLowerCase()));
      const map = {};
      sorted.forEach((u, i) => {
        map[u.personGuid] = palette[i % palette.length];
      });
      // Channel rows look up avatarColors[threadId], not a personGuid, so add those entries too.
      (this.channel_threads || []).forEach((t, i) => {
        if(t.threadId) map[t.threadId] = palette[i % palette.length];
      });
      return map;
    },
    shared_threads() {
      const guid = this.open_contact_guid;
      if(!guid) return [];
      const self = this;
      // A thread is "shared" with this contact when the contact is one of its participants.
      // These three lists are all the current user's own threads, so a contact match implies
      // a thread both people are in. Each row is tagged with _displayType so the MessageThread
      // template renders the right variant (DM avatars vs. channel/object thread avatars).
      const sharedWithContact = list => (list || []).filter(t =>
        self.$FUSarrayHasEntries(t.participantIds) && t.participantIds.includes(guid)
      );
      const dms = sharedWithContact(this.user_threads)
        .map(t => Object.assign({}, t, { _displayType: 'Messages' }));
      const channels = sharedWithContact(this.channel_threads)
        .map(t => Object.assign({}, t, { _displayType: 'Channels' }));
      // Object threads cover InventoryVehicle, Appraisal, etc. Exclude TACChat and SystemAI_
      // threads — those are support/AI surfaces, not peer-shareable chats (same exclusion the
      // MessengerWindow "Threads" tab applies in recent_threads_display).
      const threads = sharedWithContact(this.object_threads)
        .filter(t => t.objectType !== 'TACChat' && !(t.objectType || '').startsWith('SystemAI_'))
        .map(t => Object.assign({}, t, { _displayType: 'Threads' }));
      return dms.concat(channels, threads).sort((a, b) => {
        const aDate = new Date(a.lastMessageAt || a.createdAt || 0);
        const bDate = new Date(b.lastMessageAt || b.createdAt || 0);
        return bDate - aDate;
      });
    },
    shared_threads_with_names() {
      const contactName = this.contact?.fullName || 'Unknown';
      const contactGuid = this.open_contact_guid;
      const selfGuid = this.current_user_guid;
      return this.shared_threads.map(t => {
        // Channels and object threads carry their own display name from the API — leave it alone.
        // Only DMs get a synthesized "DM with [name]" label, since DMs have no name of their own.
        if(t._displayType !== 'Messages') return t;
        const otherCount = (t.participantIds || []).filter(id =>
          id !== selfGuid && id !== contactGuid && id.indexOf('system_') < 0
        ).length;
        const name = otherCount > 0
          ? `DM with ${contactName} and ${otherCount} more`
          : `DM with ${contactName}`;
        return { ...t, name };
      });
    },
  },
  methods: {
    sendMessage() {
      if(!this.contact) return;
      // Guard: messaging-disabled contacts shouldn't be able to start a thread.
      if(!this.is_messaging_enabled) return;
      // Leave a breadcrumb so Back from the New Message screen returns to this profile.
      this.contact_return_guid = this.contact.personGuid;
      // Hand off the recipient via the store; CreateThread reads + clears it on mount.
      this.pending_new_message_recipient_guid = this.contact.personGuid;
      this.open_contact_guid = null;
      this.internal_messaging_state = 'NewMessage';
    },
    addToCustomerChat() {
      // TODO: open a dropdown to select a customer chat and add this contact to it.
    },
    openSharedThread(threadId) {
      if(!threadId) return;
      // Opened from a thread/channel participant list — shared chats are display-only here to avoid
      // navigation loops. The rows are rendered non-clickable (shared_chats_clickable); this guard
      // is defense-in-depth.
      if(!this.shared_chats_clickable) return;
      // Leave a breadcrumb so Back from the opened chat returns to this profile.
      this.contact_return_guid = this.open_contact_guid;
      // MessengerWindow (still mounted behind this view via v-show) owns the open-thread
      // logic — joining, loading messages, and setting the back-button nav state. Hand the
      // thread off over the event bus; openThread flips internal_messaging_state to a
      // back-button state, which unmounts this view (clearing open_contact_guid in
      // beforeUnmount) and reveals the messages surface.
      window.event_bus.$emit('MESSENGER-OPEN-THREAD', threadId);
    },
  },
}
</script>
